import { HttpClient, HttpHeaders } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { APP_CONFIG } from "../../../core/config/app-config";
import {
    FormAnswerType,
    FormQuestion,
    FormQuestionType,
    FormSubsection,
    ManagedForm,
} from "../models/form.models";

interface FormResponse {
    title?: string;
    formSubsections?: string[];
    signatureSections?: ManagedForm["signatureSections"];
    alterationDate?: number;
    status?: string;
    clientId?: string;
    objectId?: string;
    gmvid?: string;
}

interface SubsectionResponse {
    subsectionTitle?: string;
    formQuestions?: Array<Partial<FormQuestion>>;
    alterationDate?: number;
    clientId?: string;
    objectId?: string;
    gmvid?: string;
}

interface UploadResponse {
    imageKey: string;
}

@Injectable({ providedIn: "root" })
export class FormsApiService {
    private readonly http = inject(HttpClient);
    private readonly config = inject(APP_CONFIG);
    private readonly workflowUrl = `${this.config.apiBaseUrl}/servworkflow`;

    loadForms(): Observable<ManagedForm[]> {
        return this.http
            .get<FormResponse[]>(`${this.workflowUrl}/form`)
            .pipe(
                map((forms) =>
                    forms
                        .map((form) => this.mapForm(form))
                        .sort((a, b) => a.title.localeCompare(b.title))
                )
            );
    }

    loadSubsections(): Observable<FormSubsection[]> {
        return this.http
            .get<SubsectionResponse[]>(`${this.workflowUrl}/form_subsection`)
            .pipe(
                map((items) => items.map((item) => this.mapSubsection(item)))
            );
    }

    createForm(title: string): Observable<ManagedForm> {
        return this.http
            .post<FormResponse>(`${this.workflowUrl}/form/send`, { title })
            .pipe(map((form) => this.mapForm(form)));
    }

    duplicateForm(form: ManagedForm): Observable<ManagedForm> {
        return this.http
            .post<FormResponse>(
                `${this.workflowUrl}/form/duplicate/${encodeURIComponent(form.gmvid)}`,
                { title: `${form.title} - Copy` }
            )
            .pipe(map((response) => this.mapForm(response)));
    }

    updateForm(form: ManagedForm): Observable<ManagedForm> {
        return this.http
            .patch<FormResponse>(
                `${this.workflowUrl}/form/update/${encodeURIComponent(form.gmvid)}`,
                { ...form, formSubsections: form.formSubsectionsIds }
            )
            .pipe(map((response) => this.mapForm({ ...form, ...response })));
    }

    deleteForm(formId: string): Observable<unknown> {
        return this.http.delete(
            `${this.workflowUrl}/form/${encodeURIComponent(formId)}`
        );
    }

    createSubsection(title: string): Observable<FormSubsection> {
        return this.http
            .post<SubsectionResponse>(
                `${this.workflowUrl}/form_subsection/send`,
                {
                    subsectionTitle: title,
                    alterationDate: Date.now(),
                    formQuestions: [],
                }
            )
            .pipe(map((item) => this.mapSubsection(item)));
    }

    updateSubsection(
        formId: string,
        subsection: FormSubsection
    ): Observable<FormSubsection> {
        return this.http
            .patch<SubsectionResponse>(
                `${this.workflowUrl}/form_subsection/update/${encodeURIComponent(subsection.gmvid)}?form_id=${encodeURIComponent(formId)}`,
                {
                    ...subsection,
                    _id: subsection.gmvid,
                    formQuestions: subsection.formQuestions,
                }
            )
            .pipe(map((item) => this.mapSubsection(item)));
    }

    deleteSubsection(
        subsectionId: string,
        formId: string
    ): Observable<unknown> {
        return this.http.delete(
            `${this.workflowUrl}/form_subsection/${encodeURIComponent(subsectionId)}/in/${encodeURIComponent(formId)}`
        );
    }

    uploadQuestionImage(file: File, subsectionId: string): Observable<string> {
        const extension =
            file.name.split(".").pop()?.toLocaleLowerCase() ?? "bin";
        const filename = `${Date.now()}_${subsectionId}.${extension}`;
        const headers = new HttpHeaders({ "x-filename": filename });
        const body = new FormData();
        body.append("file", file, filename);
        return this.http
            .post<UploadResponse>(
                `${this.config.apiBaseUrl}/servimages/upload/workflow`,
                body,
                { headers }
            )
            .pipe(map((response) => response.imageKey));
    }

    private mapForm(response: FormResponse): ManagedForm {
        const id = response.gmvid || response.objectId || "";
        return {
            gmvid: id,
            objectId: response.objectId || id,
            clientId: response.clientId ?? "",
            alterationDate: response.alterationDate ?? 0,
            formSubsectionsIds: response.formSubsections ?? [],
            signatureSections: response.signatureSections ?? [],
            status: response.status === "pushed" ? "pushed" : "submitted",
            title: response.title?.trim() || id,
        };
    }

    private mapSubsection(response: SubsectionResponse): FormSubsection {
        const id = response.gmvid || response.objectId || "";
        return {
            gmvid: id,
            objectId: response.objectId || id,
            clientId: response.clientId ?? "",
            alterationDate: response.alterationDate ?? 0,
            subsectionTitle: response.subsectionTitle?.trim() || id,
            formQuestions: (response.formQuestions ?? []).map((question) => ({
                gemQuestionSort: (question.gemQuestionSort ??
                    "open") as FormQuestionType,
                answerSort: (question.answerSort ?? "text") as FormAnswerType,
                questions: question.questions ?? [],
                mandatory: question.mandatory ?? false,
                answers: question.answers ?? [],
                placeholder: question.placeholder ?? "",
                table: question.table ?? [],
            })),
        };
    }
}
